import React, {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { unstable_batchedUpdates } from "react-dom";
import type { RoleName } from "common/authorization";
import { AuthContext } from "../../../../Authorization/AuthContext";
import type { PathArray } from "./FieldPathContext/FieldPathContext";
import { arePathsEqual } from "./FieldPathContext/path-utils";
import type { ExistingSubmissionType } from "./types";

export type CanCreateGroup = {
  id: string;
  name: string;
  memberType: string;
};

export enum SUBMISSIONS_BUILDER_MODE {
  BUILD = "BUILD",
  VERSION_HISTORY = "VERSION_HISTORY",
}

export interface SubmissionsBuilderContextValue {
  existingSubmissionType: Maybe<ExistingSubmissionType> | undefined;
  selectedVersionId: string | undefined;
  setSelectedVersionId: (versionId: string | undefined) => void;
  selectedVersion: ExistingSubmissionType["versions"][number] | undefined;
  canCreateRoles: Array<RoleName>;
  setCanCreateRoles: (roles: Array<RoleName>) => void;
  canCreateGroups: Array<CanCreateGroup>;
  setCanCreateGroups: (groups: Array<CanCreateGroup>) => void;
  mode: SUBMISSIONS_BUILDER_MODE;
  setMode: (mode: SUBMISSIONS_BUILDER_MODE) => void;
  selectedField: PathArray | undefined;
  selectField: (fieldPath: PathArray | undefined) => void;
}

const SubmissionsBuilderContext = createContext<
  SubmissionsBuilderContextValue | undefined
>(undefined);

type SubmissionsBuilderContextProviderProps = PropsWithChildren<{
  existingSubmissionType?: Maybe<ExistingSubmissionType>;
}>;

export function SubmissionsBuilderContextProvider({
  existingSubmissionType,
  children,
}: SubmissionsBuilderContextProviderProps) {
  const { account } = useContext(AuthContext);

  const accountUserGroups = account?.userGroups ?? [];
  const initialCanCreateGroups = accountUserGroups.filter(userGroup =>
    existingSubmissionType?.canCreateGroups.find(
      canCreateGroup => canCreateGroup.id === userGroup.id,
    ),
  );
  const [canCreateGroups, setCanCreateGroups] = useState<Array<CanCreateGroup>>(
    initialCanCreateGroups,
  );

  const [canCreateRoles, setCanCreateRoles] = useState<Array<RoleName>>(
    existingSubmissionType?.canCreate ?? [],
  );

  const [mode, setMode] = useState<SUBMISSIONS_BUILDER_MODE>(
    SUBMISSIONS_BUILDER_MODE.BUILD,
  );

  const updateMode = useCallback(
    (newMode: SUBMISSIONS_BUILDER_MODE) => {
      unstable_batchedUpdates(() => {
        setMode(newMode);

        if (newMode === SUBMISSIONS_BUILDER_MODE.VERSION_HISTORY) {
          setSelectedVersionId(existingSubmissionType?.currentVersion?.id);
        }
      });
    },
    [existingSubmissionType?.currentVersion?.id],
  );

  const [selectedVersionId, setSelectedVersionId] = useState<
    string | undefined
  >(existingSubmissionType?.currentVersion?.id);

  const resolvedSelectedVersionId =
    selectedVersionId ?? existingSubmissionType?.currentVersion?.id;

  const resolvedSelectedVersion = useMemo(
    () =>
      existingSubmissionType?.versions.find(
        version => version.id === resolvedSelectedVersionId,
      ),
    [existingSubmissionType?.versions, resolvedSelectedVersionId],
  );

  const [selectedField, setSelectedField] = useState<PathArray | undefined>();

  const value: SubmissionsBuilderContextValue = useMemo(
    () => ({
      existingSubmissionType,
      selectedVersionId: resolvedSelectedVersionId,
      setSelectedVersionId,
      selectedVersion: resolvedSelectedVersion,
      canCreateRoles,
      setCanCreateRoles,
      canCreateGroups,
      setCanCreateGroups,
      mode,
      setMode: updateMode,
      selectedField,
      selectField: setSelectedField,
    }),
    [
      existingSubmissionType,
      resolvedSelectedVersionId,
      resolvedSelectedVersion,
      canCreateRoles,
      canCreateGroups,
      mode,
      updateMode,
      selectedField,
    ],
  );

  return (
    <SubmissionsBuilderContext.Provider value={value}>
      {children}
    </SubmissionsBuilderContext.Provider>
  );
}

export function useSubmissionsBuilderContext() {
  const context = useContext(SubmissionsBuilderContext);

  if (!context) {
    throw new Error(
      "useSubmissionsBuilderContext must be used within a SubmissionsBuilderContextProvider",
    );
  }
  return context;
}

export function useSubmissionsBuilderMode() {
  const { mode, setMode } = useSubmissionsBuilderContext();
  return [mode, setMode] as const;
}

export function isFieldSelected(
  currentPathArray: PathArray,
  selectedPathArray: PathArray | undefined,
) {
  return (
    !!selectedPathArray && arePathsEqual(currentPathArray, selectedPathArray)
  );
}
